import { store, toggleLessonComplete, toggleVerseMemorized, toggleActionItem, saveReflection, submitQuizAttempt } from '../store.js';
import { showToast } from '../utils/toast.js';
import confetti from 'canvas-confetti';

let activeLessonTab = 'know';

export function renderLessonDetail(container, lessonId) {
  const lessons = store.lessons;
  const currentIdx = lessons.findIndex(l => String(l.id) === String(lessonId) || String(l.wp_id) === String(lessonId) || l.slug === lessonId);
  const lesson = currentIdx >= 0 ? lessons[currentIdx] : lessons[0];

  if (!lesson) {
    container.innerHTML = `<div class="content-container"><h2>Lesson not found</h2><a href="#course" class="btn-primary">Return to Catalog</a></div>`;
    return;
  }

  const prevLesson = currentIdx > 0 ? lessons[currentIdx - 1] : null;
  const nextLesson = currentIdx < lessons.length - 1 ? lessons[currentIdx + 1] : null;

  const isCompleted = store.progress[lesson.id]?.is_completed || false;
  const isVerseMemorized = store.progress[lesson.id]?.verse_memorized || false;
  const actionsDone = store.progress[lesson.id]?.actions_completed || [];
  const savedReflection = store.reflections[lesson.id]?.response_text || '';
  const shareWithCohort = store.reflections[lesson.id]?.share_with_cohort || false;
  const quiz = store.quizzes.find(q => q.lesson_id === lesson.id || q.id === lesson.quiz?.id) || lesson.quiz;

  const currentModule = (store.modules || []).find(m => m.id === lesson.module_id || m.title === lesson.module_name);
  const moduleName = lesson.module_name || currentModule?.title || 'Curriculum';

  const pdfUrl = (lesson.pdf_url && !lesson.pdf_url.includes('wp-content'))
    ? lesson.pdf_url
    : `/discipleship/Discipleship-Walk-Together-Ministries-Lesson-${lesson.overall_order}.pdf`;

  const hasSections = lesson.sections && Object.values(lesson.sections).some(v => Boolean(v && v.length > 0));

  // Default tab selection
  if (!hasSections) {
    activeLessonTab = 'overview';
  } else if (activeLessonTab === 'overview') {
    activeLessonTab = 'know';
  }

  container.innerHTML = `
    <div class="lesson-view-container">
      <!-- BREADCRUMBS -->
      <nav class="lesson-breadcrumbs">
        <a href="#course"><i class="fa-solid fa-arrow-left"></i> Curriculum</a>
        <span>/</span>
        <span>${moduleName}</span>
        <span>/</span>
        <span style="color:var(--text-primary); font-weight:600;">Lesson ${lesson.overall_order}</span>
      </nav>

      <!-- LESSON HEADER -->
      <div class="lesson-view-header">
        <div>
          <div style="display:flex; align-items:center; gap:0.6rem; margin-bottom:0.6rem; flex-wrap:wrap;">
            <span class="hero-badge" style="margin:0;">
              Lesson ${lesson.overall_order} of 23
            </span>
            <span style="font-size:0.85rem; font-weight:600; color:var(--text-secondary);">
              <i class="fa-solid fa-folder"></i> ${moduleName}
            </span>
            ${pdfUrl ? `
              <a href="${pdfUrl}" target="_blank" rel="noopener noreferrer" class="btn-secondary" style="padding:0.25rem 0.65rem; font-size:0.8rem; border-radius:var(--radius-full);" title="Download Printable Lesson Plan PDF">
                <i class="fa-solid fa-file-pdf" style="color:var(--accent-rose);"></i> Printable PDF Plan
              </a>
            ` : ''}
          </div>
          <h1 class="lesson-view-title">${lesson.title}</h1>
        </div>

        <div style="display:flex; align-items:center; gap:0.75rem; flex-wrap:wrap;">
          <button id="toggle-complete-btn" class="${isCompleted ? 'btn-gold' : 'btn-primary'}">
            <i class="fa-solid ${isCompleted ? 'fa-circle-check' : 'fa-check'}"></i>
            ${isCompleted ? 'Completed' : 'Mark Complete'}
          </button>
          
          <div class="lesson-nav-controls">
            ${prevLesson ? `
              <a href="#lesson/${prevLesson.id}" class="icon-btn" title="Previous: ${prevLesson.title}">
                <i class="fa-solid fa-chevron-left"></i>
              </a>
            ` : ''}
            ${nextLesson ? `
              <a href="#lesson/${nextLesson.id}" class="icon-btn" title="Next: ${nextLesson.title}">
                <i class="fa-solid fa-chevron-right"></i>
              </a>
            ` : ''}
          </div>
        </div>
      </div>

      <!-- TABS NAVIGATION -->
      ${hasSections ? `
        <div class="lesson-tabs-nav" id="lesson-tabs-list">
          <button class="lesson-tab-btn ${activeLessonTab === 'know' ? 'active' : ''}" data-tab="know">
            <i class="fa-solid fa-book-open"></i> 1. Know
          </button>
          <button class="lesson-tab-btn ${activeLessonTab === 'feel' ? 'active' : ''}" data-tab="feel">
            <i class="fa-solid fa-heart"></i> 2. Feel (Reflection)
          </button>
          <button class="lesson-tab-btn ${activeLessonTab === 'do' ? 'active' : ''}" data-tab="do">
            <i class="fa-solid fa-list-check"></i> 3. Do (Action Steps)
          </button>
          ${lesson.verse_ref ? `
            <button class="lesson-tab-btn ${activeLessonTab === 'verse' ? 'active' : ''}" data-tab="verse">
              <i class="fa-solid fa-bookmark"></i> 4. Memory Verse
            </button>
          ` : ''}
          <button class="lesson-tab-btn ${activeLessonTab === 'pray' ? 'active' : ''}" data-tab="pray">
            <i class="fa-solid fa-hands-praying"></i> 5. Pray
          </button>
          <button class="lesson-tab-btn ${activeLessonTab === 'group' ? 'active' : ''}" data-tab="group">
            <i class="fa-solid fa-users"></i> 6. Group Exercise
          </button>
          <button class="lesson-tab-btn ${activeLessonTab === 'quiz' ? 'active' : ''}" data-tab="quiz">
            <i class="fa-solid fa-circle-question"></i> Check-in & Quiz
          </button>
          ${lesson.sections.additional_resources ? `
            <button class="lesson-tab-btn ${activeLessonTab === 'resources' ? 'active' : ''}" data-tab="resources">
              <i class="fa-solid fa-link"></i> Resources
            </button>
          ` : ''}
        </div>
      ` : ''}

      <!-- TAB CONTENT PANES -->
      <div id="tab-content-root">
        ${renderTabContent(lesson, activeLessonTab, isVerseMemorized, actionsDone, savedReflection, shareWithCohort, quiz)}
      </div>

      <!-- BOTTOM NAVIGATION FOOTER -->
      <div style="display:flex; justify-content:space-between; align-items:center; margin-top:2.5rem; padding-top:1.5rem; border-top:1px solid var(--border-subtle); flex-wrap:wrap; gap:1rem;">
        ${prevLesson ? `
          <a href="#lesson/${prevLesson.id}" class="btn-secondary">
            <i class="fa-solid fa-arrow-left"></i> Previous: ${prevLesson.title}
          </a>
        ` : `<div></div>`}
        
        ${nextLesson ? `
          <a href="#lesson/${nextLesson.id}" class="btn-primary">
            Next: ${nextLesson.title} <i class="fa-solid fa-arrow-right"></i>
          </a>
        ` : `
          <a href="#course" class="btn-gold">
            <i class="fa-solid fa-award"></i> View Course Completion
          </a>
        `}
      </div>
    </div>
  `;

  // Attach Tab Switchers
  const tabs = container.querySelectorAll('[data-tab]');
  tabs.forEach(t => {
    t.onclick = () => {
      activeLessonTab = t.getAttribute('data-tab');
      renderLessonDetail(container, lesson.id);
    };
  });

  // Attach Complete Button
  const completeBtn = container.querySelector('#toggle-complete-btn');
  if (completeBtn) {
    completeBtn.onclick = async () => {
      const nowDone = await toggleLessonComplete(lesson.id);
      if (nowDone) {
        confetti({
          particleCount: 80,
          spread: 60,
          origin: { y: 0.7 }
        });
        showToast(`Congratulations! Marked Lesson ${lesson.overall_order} as complete!`, 'success');
      } else {
        showToast(`Lesson marked as incomplete`, 'info');
      }
      renderLessonDetail(container, lesson.id);
    };
  }

  attachTabInteractivity(container, lesson, quiz);
}

function renderTabContent(lesson, tab, isVerseMemorized, actionsDone, savedReflection, shareWithCohort, quiz) {
  if (tab === 'overview' || !lesson.sections || Object.keys(lesson.sections).length === 0) {
    const pdfUrl = (lesson.pdf_url && !lesson.pdf_url.includes('wp-content'))
      ? lesson.pdf_url
      : `/discipleship/Discipleship-Walk-Together-Ministries-Lesson-${lesson.overall_order}.pdf`;
    const overview = lesson.overview_content
      ? lesson.overview_content.replace(/https:\/\/discipleship\.walktogetherministries\.com\/wp-content\/uploads\/[^\"]+?\.pdf/g, pdfUrl)
      : '<p>Lesson contents.</p>';

    return `
      <div class="lesson-tab-pane">
        <div class="rich-text">
          ${overview}
        </div>
      </div>
    `;
  }

  if (tab === 'know') {
    return `
      <div class="lesson-tab-pane">
        <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:1.5rem; border-bottom:1px solid var(--border-subtle); padding-bottom:1rem;">
          <div>
            <h3 style="font-family:var(--font-display); font-size:1.4rem; font-weight:700;">
              <i class="fa-solid fa-book-open" style="color:var(--accent-blue);"></i> Know: Scripture Readings & Foundations
            </h3>
            <p style="color:var(--text-secondary); font-size:0.9rem; margin-top:0.25rem;">
              Read through these scripture passages carefully and contemplate the biblical truths.
            </p>
          </div>
        </div>

        <div class="rich-text">
          ${lesson.sections.know || '<p>No content provided for this section.</p>'}
        </div>
      </div>
    `;
  }

  if (tab === 'feel') {
    return `
      <div class="lesson-tab-pane">
        <div style="margin-bottom:1.5rem;">
          <h3 style="font-family:var(--font-display); font-size:1.4rem; font-weight:700;">
            <i class="fa-solid fa-heart" style="color:var(--accent-rose);"></i> Feel: Heart Reflection & Journal
          </h3>
          <p style="color:var(--text-secondary); font-size:0.9rem; margin-top:0.25rem;">
            Take a moment to reflect on your honest beliefs, struggles, and emotions regarding this lesson.
          </p>
        </div>

        <div class="reflection-box">
          <div style="font-weight:600; margin-bottom:0.5rem; display:flex; align-items:center; gap:0.5rem;">
            <i class="fa-solid fa-feather-pointed" style="color:var(--accent-gold);"></i>
            Your Personal Reflection
          </div>
          <p style="color:var(--text-secondary); font-size:0.9rem;">
            How did this week's scripture impact you? Where do you feel convicted, comforted, or called to grow?
          </p>

          <textarea 
            id="reflection-input" 
            class="reflection-textarea" 
            placeholder="Type your personal reflection or journal thoughts here..."
          >${savedReflection}</textarea>

          <div style="display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:1rem;">
            <label style="display:flex; align-items:center; gap:0.5rem; font-size:0.9rem; color:var(--text-secondary); cursor:pointer;">
              <input type="checkbox" id="cohort-share-checkbox" ${shareWithCohort ? 'checked' : ''} style="width:18px; height:18px;" />
              Share reflection with <strong>${store.currentGroup ? store.currentGroup.name : 'my Small Group / Cohort'}</strong>
            </label>

            <button id="save-reflection-btn" class="btn-primary">
              <i class="fa-solid fa-floppy-disk"></i> Save Reflection
            </button>
          </div>
        </div>
      </div>
    `;
  }

  if (tab === 'do') {
    // Parse bullet points into checklist
    const rawDo = lesson.sections.do || '';
    const doItems = extractListItems(rawDo);

    return `
      <div class="lesson-tab-pane">
        <div style="margin-bottom:1.5rem;">
          <h3 style="font-family:var(--font-display); font-size:1.4rem; font-weight:700;">
            <i class="fa-solid fa-list-check" style="color:var(--accent-emerald);"></i> Do: Practical Action Steps & Spiritual Disciplines
          </h3>
          <p style="color:var(--text-secondary); font-size:0.9rem; margin-top:0.25rem;">
            Faith without works is dead. Put what you have learned into practice throughout your week.
          </p>
        </div>

        <div class="action-checklist">
          ${doItems.map((item, idx) => {
            const isChecked = actionsDone.includes(idx);
            return `
              <div class="action-check-item ${isChecked ? 'checked' : ''}" data-action-idx="${idx}">
                <div class="action-check-box">
                  ${isChecked ? '<i class="fa-solid fa-check"></i>' : ''}
                </div>
                <div style="flex:1; font-size:1rem; color:var(--text-primary);">
                  ${item}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  if (tab === 'verse') {
    return `
      <div class="lesson-tab-pane">
        <div style="text-align:center; margin-bottom:1.5rem;">
          <h3 style="font-family:var(--font-display); font-size:1.4rem; font-weight:700;">
            <i class="fa-solid fa-bookmark" style="color:var(--accent-gold);"></i> Memory Verse Flashcard
          </h3>
          <p style="color:var(--text-secondary); font-size:0.9rem; margin-top:0.25rem;">
            Click the card to flip and reveal the full scripture passage. Practice reciting it aloud!
          </p>
        </div>

        <div class="flipcard-stage">
          <div class="flipcard-flipper" id="flipper-card" title="Click to flip card">
            <div class="flipcard-front">
              <div class="brand-icon-box" style="margin-bottom:1.5rem; background:rgba(255,255,255,0.2);">
                <i class="fa-regular fa-lightbulb"></i>
              </div>
              <div class="verse-ref-large">${lesson.verse_ref}</div>
              <div class="verse-prompt-hint">
                <i class="fa-solid fa-arrows-rotate"></i> Click card to flip and recite
              </div>
            </div>

            <div class="flipcard-back">
              <div style="font-size:0.85rem; font-weight:700; color:var(--accent-gold); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:0.75rem;">
                ${lesson.verse_ref} (NIV)
              </div>
              <div class="verse-text-quote">
                “${lesson.verse_text}”
              </div>
              <div class="verse-prompt-hint" style="opacity:0.75;">
                <i class="fa-solid fa-arrows-rotate"></i> Click to flip back
              </div>
            </div>
          </div>
        </div>

        <div class="verse-controls-row">
          <button id="listen-verse-btn" class="btn-secondary" title="Listen to verse audio">
            <i class="fa-solid fa-volume-high"></i> Listen Aloud
          </button>
          
          <button id="copy-verse-btn" class="btn-secondary" title="Copy verse to clipboard">
            <i class="fa-regular fa-copy"></i> Copy Verse
          </button>

          <button id="toggle-verse-memorized-btn" class="${isVerseMemorized ? 'btn-gold' : 'btn-primary'}">
            <i class="fa-solid ${isVerseMemorized ? 'fa-star' : 'fa-check'}"></i>
            ${isVerseMemorized ? 'Memorized!' : 'Mark as Memorized'}
          </button>
        </div>
      </div>
    `;
  }

  if (tab === 'pray') {
    return `
      <div class="lesson-tab-pane">
        <div style="margin-bottom:1.5rem;">
          <h3 style="font-family:var(--font-display); font-size:1.4rem; font-weight:700;">
            <i class="fa-solid fa-hands-praying" style="color:var(--accent-purple);"></i> Pray: Guided Prayer
          </h3>
          <p style="color:var(--text-secondary); font-size:0.9rem; margin-top:0.25rem;">
            Lift these thoughts up to God in heartfelt prayer, either individually or together with your small group.
          </p>
        </div>

        <div style="background:linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%); border:1px solid rgba(139, 92, 246, 0.25); border-radius:var(--radius-xl); padding:2rem; font-family:var(--font-scripture); font-size:1.35rem; font-style:italic; line-height:1.75; color:var(--text-primary);">
          ${lesson.sections.pray || '<p>No guided prayer text for this lesson.</p>'}
        </div>
      </div>
    `;
  }

  if (tab === 'group') {
    return `
      <div class="lesson-tab-pane">
        <div style="margin-bottom:1.5rem;">
          <h3 style="font-family:var(--font-display); font-size:1.4rem; font-weight:700;">
            <i class="fa-solid fa-users" style="color:var(--accent-blue);"></i> Group Exercise & Cohort Discussion
          </h3>
          <p style="color:var(--text-secondary); font-size:0.9rem; margin-top:0.25rem;">
            When you gather with your discipleship group, use these discussion prompts to encourage authentic sharing.
          </p>
        </div>

        <div class="rich-text">
          ${lesson.sections.group_exercise || '<p>Review the lesson together and pray over one another.</p>'}
        </div>
      </div>
    `;
  }

  if (tab === 'quiz') {
    return `
      <div class="lesson-tab-pane">
        <div class="quiz-container">
          <div style="text-align:center; margin-bottom:2rem;">
            <h3 style="font-family:var(--font-display); font-size:1.5rem; font-weight:700;">
              ${lesson.title} - Check-in & Quiz
            </h3>
            <p style="color:var(--text-secondary); font-size:0.9rem; margin-top:0.25rem;">
              Test your recall of the scripture, key truths, and personal reflection.
            </p>
          </div>

          <form id="quiz-form">
            ${(quiz?.questions || []).map((q, qIdx) => {
              return `
                <div class="question-card" data-q-id="${q.id}">
                  <div style="font-size:0.75rem; font-weight:700; color:var(--accent-blue); text-transform:uppercase; margin-bottom:0.5rem;">
                    Question ${qIdx + 1} of ${quiz.questions.length}
                  </div>
                  <div class="question-prompt">${q.question_text}</div>

                  ${q.question_type === 'multiple_choice' ? `
                    <div class="options-list">
                      ${(q.options || []).map((opt, optIdx) => `
                        <label class="option-btn" for="q-${q.id}-opt-${optIdx}">
                          <input type="radio" name="question_${q.id}" id="q-${q.id}-opt-${optIdx}" value="${opt}" style="margin-right:8px;" />
                          <span>${opt}</span>
                        </label>
                      `).join('')}
                    </div>
                  ` : `
                    <textarea 
                      class="reflection-textarea" 
                      name="question_${q.id}" 
                      placeholder="Write your answer..." 
                      style="min-height:100px;"
                    ></textarea>
                  `}
                  <div class="quiz-feedback" style="display:none; margin-top:1rem; padding:0.75rem; border-radius:var(--radius-md); font-size:0.9rem;"></div>
                </div>
              `;
            }).join('')}

            <div style="text-align:center; margin-top:2rem;">
              <button type="submit" id="submit-quiz-btn" class="btn-primary" style="padding:0.75rem 2rem; font-size:1rem;">
                <i class="fa-solid fa-paper-plane"></i> Submit Check-in
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (tab === 'resources') {
    return `
      <div class="lesson-tab-pane">
        <div style="margin-bottom:1.5rem;">
          <h3 style="font-family:var(--font-display); font-size:1.4rem; font-weight:700;">
            <i class="fa-solid fa-link" style="color:var(--accent-blue);"></i> Additional Study Resources
          </h3>
          <p style="color:var(--text-secondary); font-size:0.9rem; margin-top:0.25rem;">
            Deepen your understanding with these recommended books, articles, and external tools.
          </p>
        </div>

        <div class="rich-text">
          ${lesson.sections.additional_resources}
        </div>
      </div>
    `;
  }

  return '<div></div>';
}

function extractListItems(html) {
  const matches = [...html.matchAll(/<li>(.*?)<\/li>/gis)];
  if (matches.length > 0) {
    return matches.map(m => m[1].replace(/<[^>]+>/g, '').trim()).filter(t => t.length > 0);
  }
  // Fallback split by lines
  return html.split('\n').map(l => l.replace(/<[^>]+>/g, '').trim()).filter(l => l.length > 0);
}

function attachTabInteractivity(container, lesson, quiz) {
  // Flip Card
  const flipper = container.querySelector('#flipper-card');
  if (flipper) {
    flipper.onclick = () => {
      flipper.classList.toggle('flipped');
    };
  }

  // Text to speech for verse
  const listenBtn = container.querySelector('#listen-verse-btn');
  if (listenBtn && lesson.verse_text) {
    listenBtn.onclick = () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(`${lesson.verse_ref}. ${lesson.verse_text}`);
        utterance.rate = 0.95;
        window.speechSynthesis.speak(utterance);
        showToast('Playing scripture audio...', 'info');
      } else {
        showToast('Speech audio not supported on this browser', 'error');
      }
    };
  }

  // Copy verse
  const copyBtn = container.querySelector('#copy-verse-btn');
  if (copyBtn && lesson.verse_text) {
    copyBtn.onclick = async () => {
      await navigator.clipboard.writeText(`${lesson.verse_ref} - "${lesson.verse_text}"`);
      showToast('Verse copied to clipboard!', 'success');
    };
  }

  // Toggle verse memorized
  const memorizedBtn = container.querySelector('#toggle-verse-memorized-btn');
  if (memorizedBtn) {
    memorizedBtn.onclick = async () => {
      const nowMemorized = await toggleVerseMemorized(lesson.id);
      if (nowMemorized) {
        confetti({ particleCount: 60, spread: 50 });
        showToast('Memory verse marked as memorized! Keep hiding God\'s word in your heart.', 'success');
      } else {
        showToast('Verse unmarked as memorized', 'info');
      }
      renderLessonDetail(container, lesson.id);
    };
  }

  // Save Reflection
  const saveRefBtn = container.querySelector('#save-reflection-btn');
  if (saveRefBtn) {
    saveRefBtn.onclick = async () => {
      const text = container.querySelector('#reflection-input').value.trim();
      const share = container.querySelector('#cohort-share-checkbox').checked;
      await saveReflection(lesson.id, text, share);
      showToast('Reflection saved successfully!', 'success');
    };
  }

  // Action Check Items
  const actionItems = container.querySelectorAll('.action-check-item');
  actionItems.forEach(item => {
    item.onclick = async () => {
      const idx = parseInt(item.getAttribute('data-action-idx'), 10);
      await toggleActionItem(lesson.id, idx);
      renderLessonDetail(container, lesson.id);
    };
  });

  // Quiz submission
  const quizForm = container.querySelector('#quiz-form');
  if (quizForm && quiz?.questions) {
    quizForm.onsubmit = async (e) => {
      e.preventDefault();
      const formData = new FormData(quizForm);
      let correct = 0;
      let totalGraded = 0;
      const answers = {};

      quiz.questions.forEach(q => {
        const val = formData.get(`question_${q.id}`);
        answers[q.id] = val;
        const card = container.querySelector(`[data-q-id="${q.id}"]`);
        const feedback = card.querySelector('.quiz-feedback');

        if (q.question_type === 'multiple_choice') {
          totalGraded++;
          const isCorrect = (val || '').trim().toLowerCase() === (q.correct_answer || '').trim().toLowerCase();
          if (isCorrect) correct++;

          feedback.style.display = 'block';
          feedback.style.backgroundColor = isCorrect ? 'rgba(16,185,129,0.15)' : 'rgba(244,63,94,0.15)';
          feedback.style.color = isCorrect ? 'var(--accent-emerald)' : 'var(--accent-rose)';
          feedback.innerHTML = isCorrect 
            ? `<strong>Correct!</strong> ${q.explanation || ''}` 
            : `<strong>Incorrect.</strong> Correct answer: <em>${q.correct_answer}</em>. ${q.explanation || ''}`;
        } else {
          feedback.style.display = 'block';
          feedback.style.backgroundColor = 'rgba(59,130,246,0.15)';
          feedback.style.color = 'var(--accent-blue)';
          feedback.innerHTML = `<strong>Reflection Recorded:</strong> Great thoughts! Be ready to share these with your cohort.`;
        }
      });

      const score = totalGraded > 0 ? Math.round((correct / totalGraded) * 100) : 100;
      const passed = score >= (quiz.passing_percentage || 70);

      await submitQuizAttempt(quiz.id, answers, score, passed);

      if (passed) {
        confetti({ particleCount: 70, spread: 60 });
        showToast(`Check-in passed with ${score}%!`, 'success');
      } else {
        showToast(`Score: ${score}%. Review the questions and try again.`, 'error');
      }
    };
  }
}
